import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Column, Row, Text, Icon, Button, Card } from "@amzn/meridian";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { getInspectionWorkflow } from "src/store/workflowSlice";
import InspectionService, {
    InspectionData,
    InspectionResult,
    UpdateInspectionPayload,
} from "src/services/inspectionService";
import { completedInspectionStatus } from "src/constants/constants";
import { resetInspectionCaptureState } from "src/store/inspectionCaptureSlice";
import CheckpointCard from "./CheckpointCard";
import { formatDate } from "src/utils/dateUtil";
import "./inspectionReviewPage.scss";

/** Determine overall compliance from grouped results */
const getOverallCompliance = (
    groupedResults: Record<string, InspectionResult[]>
): boolean => {
    return Object.values(groupedResults).every((results) =>
        results.every((r) => r.isCompliant)
    );
};

const InspectionReviewPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [inspectionData, setInspectionData] = useState<InspectionData | null>(null);
    // TODO: This will be used to display the taken picture when you click to view the details of
    // a compliance row
    const [presignedUrlMap, setPresignedUrlMap] = useState<Record<string, string>>({});
    const [groupedResults, setGroupedResults] = useState<Record<string, InspectionResult[]>>({});

    const { questions } = useAppSelector((state) => state.workflow);

    // Fetch workflow config if not already loaded
    useEffect(() => {
        if (!questions || questions.length === 0) {
            dispatch(getInspectionWorkflow());
        }
    }, [questions]);

    // Fetch inspection data
    useEffect(() => {
        const fetchInspection = async () => {
            try {
                const data = await InspectionService.fetchInspectionWithPresignedUrls(
                    inspectionId!
                );
                setInspectionData(data.inspectionData);
                setPresignedUrlMap(data.presignedUrlMap);
                setGroupedResults(data.groupedResults);
            } catch (err: any) {
                console.error("Failed to fetch inspection data:", err);
            }
        };
        fetchInspection();
    }, [inspectionId]);

    const submitInspection = async () => {
        try {
            await InspectionService.completeInspection({
                inspectionId: inspectionId,
                templateId: inspectionData?.templateId,
                templateName: inspectionData?.templateName,
                siteCode: inspectionData?.siteCode,
                status: completedInspectionStatus,
                createdBy: inspectionData?.createdBy,
                createdAt: inspectionData?.createdAt,
            } as UpdateInspectionPayload);
            navigate("/home");
        } catch (error) {
            console.error("Failed to submit inspection:", error);
        }
    };

    const getQuestionText = (questionId: string): string => {
        const question = questions.find((q) => q.id === questionId);
        return question?.text || questionId;
    };

    if (!inspectionData) {
        return (
            <Column
                height="100vh"
                alignmentVertical="center"
                alignmentHorizontal="center"
            >
                <Text type="h200">Loading inspection data...</Text>
            </Column>
        );
    }

    const overallCompliant = getOverallCompliance(groupedResults);
    const { datePart, timePart } = formatDate(inspectionData.createdAt);
    const questionEntries = Object.entries(groupedResults);

    return (
        <div className="inspection-review-page">
            <Column spacingInset="500" spacing="400">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="inspection-review-back-button"
                        onClick={() => {
                            dispatch(resetInspectionCaptureState());
                            navigate("/");
                        }}
                        role="button"
                    >
                        <Icon tokens={chevronLeftLargeTokens} color="primary" />
                    </div>
                    <Text type="b300" color="primary">
                        Inspection Review
                    </Text>
                </Row>

                {/* TODO: This will need to be dynamic based on what specific configuration is in context
                */}
                <Text type="h300" color="primary">
                    Waste Storage Inspection
                </Text>

                <Card spacingInset="none">
                    <Column spacingInset="400" spacing="200">
                        <Row spacing="200">
                            <Text type="b200" color="primary">
                                {datePart}
                            </Text>
                            <Text type="b200" color="secondary">
                                {timePart}
                            </Text>
                        </Row>
                        <Text type="b100" color="primary">
                            OBSERVATION
                        </Text>
                        <Text
                            type="h300"
                            color={overallCompliant ? "success" : "error"}
                        >
                            {overallCompliant ? "Compliant" : "Non Compliant"}
                        </Text>
                    </Column>
                </Card>

                <Text type="h200" color="primary">
                    Check points
                </Text>

                {questionEntries.map(([questionId, results], idx) => {
                    const allCompliant = results.every((r) => r.isCompliant);
                    const firstResult = results[0];
                    const isAI =
                        firstResult?.aiReasoning !== null &&
                        firstResult?.aiReasoning !== "";

                    return (
                        <CheckpointCard
                            key={questionId}
                            questionNumber={String(idx + 1).padStart(2, "0")}
                            questionText={getQuestionText(questionId)}
                            isAI={isAI}
                            allCompliant={allCompliant}
                            results={results}
                            presignedUrlMap={presignedUrlMap}
                        />
                    );
                })}

                {inspectionData.status.toLowerCase() === "in_progress" && (
                    <Button
                        type="primary"
                        size="medium"
                        onClick={submitInspection}
                    >
                        Submit Inspection
                    </Button>
                )}
            </Column>
        </div>
    );
};

export default InspectionReviewPage;
